/*
 * Copyright (c) All right reserved Anvish Inc. 2026
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */

"use client";
import React, { useEffect, useState } from "react";
import { Button } from "@heroui/button";
import { Swiper, SwiperSlide } from "swiper/react";
import Image from "next/image";
import "swiper/css";

interface GallerySectionProps {
  heading: string;
  imageList: any[];
}

const GallerySection: React.FC<GallerySectionProps> = ({
  heading,
  imageList,
}) => {
  const sliderRef = React.useRef<any>(null);
  const [isReachedEnd, setIsReachedEnd] = useState<boolean>(false);
  const [isReachedStart, setIsReachedStart] = useState(false);

  const handlePrev = () => {
    sliderRef.current.swiper.slidePrev();
  };

  const handleNext = () => {
    sliderRef.current.swiper.slideNext();
  };

  // Extreme End Position Checker
  useEffect(() => {
    if (sliderRef.current?.swiper) {
      const swiper = sliderRef.current.swiper;

      setIsReachedStart(swiper.isBeginning);
      setIsReachedEnd(swiper.isEnd);

      // Listen for changes in swiper state
      swiper.on("slideChange", () => {
        setIsReachedStart(swiper.isBeginning);
        setIsReachedEnd(swiper.isEnd);
      });
    }
  }, [sliderRef]);

  return (
    <section className="flex flex-col items-center justify-center px-4 md:px-14 xl:px-[100px] max-w-[1440px] mx-auto pb-10">
      <div className="flex flex-col gap-7 md:flex-row md:gap-14 items-start justify-center md:justify-between w-full pr-4 md:pr-14 xl:pr-[100px] max-w-[1440px] mx-auto">
        <h2 className="text-3xl md:text-4xl font-medium text-left">
          {heading}
        </h2>
        <div className="hidden md:flex w-full md:w-fit items-center justify-end gap-3 md:gap-4">
          <Button
            isIconOnly
            className="w-10 h-10 bg-transparent border-[1px] border-black rounded-full"
            isDisabled={isReachedStart}
            radius="full"
            variant="flat"
            onPress={handlePrev}
          >
            <Image
              alt="Left Arrow"
              className="w-5 h-5 object-contain rotate-180"
              height={20}
              src="/icons/arrow_left_black.svg"
              width={20}
            />
          </Button>
          <Button
            isIconOnly
            className="w-10 h-10 bg-transparent border-[1px] border-black rounded-full"
            isDisabled={isReachedEnd}
            radius="full"
            variant="flat"
            onPress={handleNext}
          >
            <Image
              alt="Left Arrow"
              className="w-5 h-5 object-contain"
              height={20}
              src="/icons/arrow_left_black.svg"
              width={20}
            />
          </Button>
        </div>
      </div>
      <div className="flex flex-col w-full mt-10">
        <Swiper
          ref={sliderRef}
          breakpoints={{
            640: {
              slidesPerView: 2,
              spaceBetween: 24,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 24,
            },
          }}
          className="w-full h-full"
          slidesPerView={1}
          spaceBetween={24}
        >
          {imageList.map((image: any, index: number) => (
            <SwiperSlide
              key={index}
              className="!flex w-fit h-full items-center justify-center"
            >
              <Image
                key={index}
                alt={image.image.title}
                className="aspect-square w-full h-[300px] md:h-[400px] object-cover"
                height={500}
                src={image.image.meta.download_url}
                width={500}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="flex md:hidden w-full items-center justify-center gap-3 md:gap-4 mt-6">
        <Button
          isIconOnly
          className="w-10 h-10 bg-[#F6F6F6] rounded-full"
          isDisabled={isReachedStart}
          radius="full"
          variant="flat"
          onPress={handlePrev}
        >
          <img
            alt="Left Arrow"
            className="w-5 h-5 object-contain rotate-180"
            height={20}
            src="/icons/arrow_left_black.svg"
            width={20}
          />
        </Button>
        <Button
          isIconOnly
          className="w-10 h-10 bg-[#F6F6F6] rounded-full"
          isDisabled={isReachedEnd}
          radius="full"
          variant="flat"
          onPress={handleNext}
        >
          <img
            alt="Left Arrow"
            className="w-5 h-5 object-contain"
            height={20}
            src="/icons/arrow_left_black.svg"
            width={20}
          />
        </Button>
      </div>
    </section>
  );
};

export default GallerySection;
